import { CategoryType } from 'eve-account-client';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { TextInputControl } from '@ver-uds/uswds-react';
import { HelpModalProps } from './HelpModal';
import LabelHint from './Enrollment/CompanyInformation/LabelHint';

type FormValues = {
  ueiSAM: string;
};

export type UEIFieldProps = {
  openModal(content: HelpModalProps['modalContent']): void;
  employerCategory: CategoryType;
};

const FIELD_NAME = 'Unique Entity Identifier (UEI)';
const CHAR_LIMIT_ERROR = `${FIELD_NAME} must be 12 characters in length. Example: 1234ABC56789`;

const UEIField = ({ employerCategory, openModal }: UEIFieldProps): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { register, errors } = useFormContext<FormValues>();
  const required = employerCategory === CategoryType.FEDERALCONTRACTOR;

  return (
    <TextInputControl
      id="uei"
      name="ueiSAM"
      className="grid-col-6 tablet:grid-col-3 desktop:grid-col-2"
      label={FIELD_NAME}
      hint="12 characters, numbers and letters only"
      labelHint={<LabelHint openModal={openModal} label="Unique Entity Identifier (UEI)" />}
      required={required}
      ref={register({
        validate: (value: string) => {
          // if UEI is required or if the user provided a value (when they weren't required to enter it), check validation
          if (required || value.trim().length > 0) {
            if (value.startsWith('0')) {
              return `${FIELD_NAME} cannot start with zero.`;
            }

            if (value.toUpperCase().includes('I')) {
              return `${FIELD_NAME} cannot contain the letter 'I'.`;
            }

            if (value.toUpperCase().includes('O')) {
              return `${FIELD_NAME} cannot contain the letter 'O'.`;
            }

            // checking for nine-digit sequences
            if (/[0-9]{9}/.test(value)) {
              return `${FIELD_NAME} cannot contain a nine-digit sequence.`;
            }

            if (value.length !== 12) {
              return CHAR_LIMIT_ERROR;
            }

            // check for non letters and numbers
            if (!/^[a-z0-9]+$/i.test(value)) {
              return `${FIELD_NAME} can only contain numbers and letters.`;
            }
          }
          return true;
        },
      })}
      errorId={errors.ueiSAM && 'uei-error'}
      errorMessage={errors.ueiSAM?.message}
    />
  );
};

export default UEIField;
